import { createContext, useState, useMemo } from "react";
import { createTheme } from "@mui/material/styles";

// color design tokens export
export const tokens = (mode) => ({
	...(mode === "dark"
		? {
			grey: {
				100: "#FAFAFA", // off-white
				200: "#E5E5E5", // off-gray
				300: "#a3a3a3",
				400: "#858585",
				500: "#666666",
				600: "#525252",
				700: "#3d3d3d",
				800: "#292929",
				900: "#141414",
			},
			primary: {
				100: "#d0d1d5",
				200: "#a1a4ab",
				300: "#727681",
				400: "#2d2d2d",
				500: "#1F1F1F", // off-black
				600: "#181818",
				700: "#121212",
				800: "#0c0c0c",
				900: "#050505",
			},
			greenAccent: {
				100: "#fdf8e4",
				200: "#fbf2c8",
				300: "#f9ebad",
				400: "#f7e491",
				500: "#F9D94C", // off-yellow used as primary accent
				600: "#c7ae3d",
				700: "#95822e",
				800: "#64571e",
				900: "#322b0f",
			},
			redAccent: {
				100: "#f8dcdb",
				200: "#f1b9b7",
				300: "#e99592",
				400: "#e2726e",
				500: "#db4f4a",
				600: "#af3f3b",
				700: "#832f2c",
				800: "#58201e",
				900: "#2c100f",
			},
			blueAccent: {
				100: "#e1e2fe",
				200: "#c3c6fd",
				300: "#a4a9fc",
				400: "#868dfb",
				500: "#6870fa",
				600: "#535ac8",
				700: "#3e4396",
				800: "#2a2d64",
				900: "#151632",
			},
		}
		: {
			grey: {
				100: "#1F1F1F", // off-black
				200: "#292929",
				300: "#3d3d3d",
				400: "#525252",
				500: "#666666",
				600: "#858585",
				700: "#a3a3a3",
				800: "#c2c2c2",
				900: "#e0e0e0",
			},
			primary: {
				100: "#050505",
				200: "#0c0c0c",
				300: "#121212",
				400: "#FAFAFA", // off-white
				500: "#E5E5E5", // off-gray
				600: "#d0d1d5",
				700: "#a1a4ab",
				800: "#727681",
				900: "#1F2A40",
			},
			greenAccent: {
				100: "#322b0f",
				200: "#64571e",
				300: "#95822e",
				400: "#c7ae3d",
				500: "#F9D94C", // off-yellow
				600: "#f7e491",
				700: "#f9ebad",
				800: "#fbf2c8",
				900: "#fdf8e4",
			},
			redAccent: {
				100: "#2c100f",
				200: "#58201e",
				300: "#832f2c",
				400: "#af3f3b",
				500: "#db4f4a",
				600: "#e2726e",
				700: "#e99592",
				800: "#f1b9b7",
				900: "#f8dcdb",
			},
			blueAccent: {
				100: "#151632",
				200: "#2a2d64",
				300: "#3e4396",
				400: "#535ac8",
				500: "#6870fa",
				600: "#868dfb",
				700: "#a4a9fc",
				800: "#c3c6fd",
				900: "#e1e2fe",
			},
		}),
});

// mui theme settings
export const themeSettings = (mode) => {
	const colors = tokens(mode);
	return {
		palette: {
			mode: mode,
			...(mode === "dark"
				? {
					// palette values for dark mode
					primary: {
						main: colors.primary[500],
					},
					secondary: {
						main: colors.greenAccent[500],
					},
					neutral: {
						dark: colors.grey[700],
						main: colors.grey[500],
						light: colors.grey[100],
					},
					background: {
						default: colors.primary[500],
						paper: colors.primary[400],
					},
				}
				: {
					// palette values for light mode
					primary: {
						main: colors.primary[400],
					},
					secondary: {
						main: colors.greenAccent[500],
					},
					neutral: {
						dark: colors.grey[700],
						main: colors.grey[500],
						light: colors.grey[100],
					},
					background: {
						default: "#FAFAFA",
						paper: "#E5E5E5",
					},
				}),
		},
		typography: {
			fontFamily: ["Josefin Sans", "BARLOW SEMI CONDENSED", "sans-serif"].join(","),
			fontSize: 12,
			h1: {
				fontFamily: ["Josefin Sans", "BARLOW SEMI CONDENSED", "sans-serif"].join(","),
				fontSize: 40,
			},
			h2: {
				fontFamily: ["Josefin Sans", "BARLOW SEMI CONDENSED", "sans-serif"].join(","),
				fontSize: 32,
			},
			h3: {
				fontFamily: ["Josefin Sans", "BARLOW SEMI CONDENSED", "sans-serif"].join(","),
				fontSize: 24,
			},
			h4: {
				fontFamily: ["Josefin Sans", "BARLOW SEMI CONDENSED", "sans-serif"].join(","),
				fontSize: 20,
			},
			h5: {
				fontFamily: ["Josefin Sans", "BARLOW SEMI CONDENSED", "sans-serif"].join(","),
				fontSize: 16,
			},
			h6: {
				fontFamily: ["Josefin Sans", "BARLOW SEMI CONDENSED", "sans-serif"].join(","),
				fontSize: 14,
			},
		},
	};
};

// context for color mode
export const ColorModeContext = createContext({
	toggleColorMode: () => { },
});

export const useMode = () => {
	const [mode, setMode] = useState("dark");

	const colorMode = useMemo(
		() => ({
			toggleColorMode: () =>
				setMode((prev) => (prev === "light" ? "dark" : "light")),
		}),
		[]
	);

	const theme = useMemo(() => createTheme(themeSettings(mode)), [mode]);
	return [theme, colorMode];
};
